<template>
  <div class='details'>
    <Loading :visible='organisationDetailLoading'>
      <template v-if='organisation && organisation.createdByUser'>
        <div class='d-md-block d-xl-none d-flex justify-content-between'>
          <FeatherIcon v-b-toggle icon='XIcon' size='24' @click='closeSidebar()' />
          <div>Organisation Details</div>
          <div><!-- Needed to prevent bootstrap issue--></div>
        </div>

        <div v-if='!showDeleteModal' class='details-tab'>
          <b-tabs align='center' class='d-b-tabs'>
            <b-tab>
              <template #title>
                <FeatherIcon icon='FileTextIcon' size='18' />
              </template>
              <div class='organisation-details-wrap'>
                <b-card-text>
                  <div class='container'>
                    <BAvatar class='d-none d-sm-block folder' size='60'
                             :text='organisation.name | firstLetter'></BAvatar>
                  </div>
                </b-card-text>
                <hr />
                <b-container class='b-card-text'>
                  <BRow class='row-section'>
                    <BCol>
                      <BRow>
                        <h6 v-if='!editDisplay' class='clear-margin'>
                          {{ organisation.name }}
                          <feather-icon
                            v-if='hasEditRights'
                            class='btn-edit'
                            icon='Edit2Icon'
                            @click='
                              editDisplay = !editDisplay
                              editConfirm = !editConfirm
                            '
                          />
                        </h6>
                        <b-input-group v-if='editDisplay'>
                          <b-form-input v-model='organisation.name' size='sm' />
                          <b-input-group-append>
                            <BButton
                              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                              :disabled='!validName'
                              size='sm'
                              :variant="validName ? 'primary' : 'danger'"
                              @click='
                                editDisplay = !editDisplay
                                editConfirm = !editConfirm
                              '
                            >
                              <FeatherIcon v-if='validName' icon='CheckIcon' />
                              <FeatherIcon v-if='!validName' icon='AlertCircleIcon' />
                            </BButton>
                          </b-input-group-append>
                        </b-input-group>
                      </BRow>
                    </BCol>
                    <br />
                  </BRow>
                  <BRow class='row-section'>
                    <BCol>
                      <BRow>
                        <h6 class='primary-color clear-margin'>Created</h6>
                      </BRow>
                      <BRow>
                        <p class='clear-margin'>{{ organisation.createdByUser.firstName }}
                          {{ organisation.createdByUser.lastName }}</p>
                      </BRow>
                      <BRow>
                        <p class='clear-margin'>{{ organisation.createdOn | luxon }}</p>
                      </BRow>
                    </BCol>
                  </BRow>
                  <BRow class='row-section'>
                    <BCol>
                      <BRow>
                        <h6 class='primary-color clear-margin'>Last modified</h6>
                      </BRow>
                      <BRow>
                        <p class='clear-margin'>{{ organisation.lastModifiedByUser.firstName }}
                          {{ organisation.lastModifiedByUser.lastName }}</p>
                      </BRow>
                      <BRow>
                        <p class='clear-margin'>{{ organisation.lastModifiedOn | luxon }}</p>
                      </BRow>
                    </BCol>
                  </BRow>
                  <BRow class='row-section'>
                    <BCol>
                      <BRow>
                        <h6 class='primary-color clear-margin'>Total Disk Usage:</h6>
                      </BRow>
                      <BRow>
                        <p class='clear-margin'>{{ `${orgReport.totalMb || ''} Mb` }}</p>
                      </BRow>
                    </BCol>
                  </BRow>
                  <BRow class='row-section'>
                    <BCol>
                      <BRow>
                        <h6 class='primary-color clear-margin'>Vaults:</h6>
                      </BRow>
                      <BRow>
                        <p class='clear-margin'>{{ orgReport.totalVaults }}</p>
                      </BRow>
                    </BCol>
                  </BRow>
                  <BRow class='row-section'>
                    <BCol>
                      <BRow>
                        <h6 class='primary-color clear-margin'>Folders:</h6>
                      </BRow>
                      <BRow>
                        <p class='clear-margin'>{{ orgReport.totalFolders }}</p>
                      </BRow>
                    </BCol>
                  </BRow>
                  <BRow class='row-section'>
                    <BCol>
                      <BRow>
                        <h6 class='primary-color clear-margin'>Documents:</h6>
                      </BRow>
                      <BRow>
                        <p class='clear-margin'>{{ orgReport.totalDocuments }}</p>
                      </BRow>
                    </BCol>
                  </BRow>
                </b-container>
                <div v-if='!organisation.deleted' class='d-flex'>
                  <b-button
                    v-ripple.320="'rgba(40, 199, 111, 0.15)'"
                    size='sm'
                    class='modal-button'
                    variant='outline-secondary'
                    @click='closeSidebar()'
                  >
                    Cancel
                  </b-button>
                  <b-button
                    v-if='hasEditRights'
                    v-ripple.320="'rgba(40, 199, 111, 0.15)'"
                    :disabled='!editConfirm'
                    size='sm'
                    variant='danger'
                    class='modal-button'
                    @click='openDeleteConfirm(true)'
                  >
                    Delete
                  </b-button>
                  <b-button
                    v-if='hasEditRights'
                    v-ripple.320="'rgba(40, 199, 111, 0.15)'"
                    :disabled='!editConfirm'
                    size='sm'
                    class='modal-button ml-auto'
                    variant='primary'
                    @click='update()'
                  >
                    Update
                  </b-button>
                </div>
              </div>
            </b-tab>
            <b-tab>
              <template #title>
                <FeatherIcon icon='ClockIcon' size='18' />
              </template>
              <div class='card-body'>
                <app-timeline v-for='audit in organisation.auditHistories' :key='audit.id' :organisation='organisation'>
                  <app-timeline-item :title='audit.action' icon='UserIcon' :time='audit.timestamp | luxon'
                                     variant='info' />
                  <br />
                </app-timeline>
              </div>
            </b-tab>
            <b-tab>
              <template #title>
                <FeatherIcon icon='LayoutIcon' size='18' />
              </template>
              <b-form>
                <b-form-group label='Primary Color'>
                  <color-picker v-model='organisation.brand.primaryColor' show-alpha></color-picker>
                </b-form-group>
                <b-form-group label='Secondary Color'>
                  <color-picker v-model='organisation.brand.secondaryColor' show-alpha></color-picker>
                </b-form-group>
                <b-form-group label='Logo'>
                  <b-form-input v-model='organisation.brand.logo'></b-form-input>
                </b-form-group>
                <b-button size='sm' class='modal-button ml-auto' variant='primary' @click='onSaveBrand'>Submit
                </b-button>
              </b-form>
            </b-tab>
            <b-tab>
              <template #title>
                <FeatherIcon icon='KeyIcon' size='18' />
              </template>
              <b-form>
                <b-form-group label='Client Id'>
                  <b-form-input v-model='organisation.clientId' disabled></b-form-input>
                </b-form-group>
                <b-form-group label='Client Secrets'>
                  <BasicButton @click='onAddSecret'>Add Secret</BasicButton>
                  <BasicBlankLine />
                  <b-list-group>
                    <b-list-group-item v-for='(item, index) of organisation.clientSecrets' :key='index'>
                      <div style='justify-content: space-between; display: flex'>
                        <div>{{ secret(index) }}</div>
                        <basic-button variant='link' @click='onRemoveSecret(index)'>
                          <i class='el-icon-close' />
                        </basic-button>
                      </div>
                    </b-list-group-item>
                  </b-list-group>
                </b-form-group>
              </b-form>
            </b-tab>
          </b-tabs>
        </div>
      </template>
      <DeleteConfirmation v-if='showDeleteModal' :entity-type='entityType' @deleteResponse='deleteEntity' />
    </Loading>
  </div>
</template>
<script>
import { mapMutations, mapState } from 'vuex'
import {
  BAvatar,
  BButton,
  BCardText,
  BCol,
  BContainer,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BRow,
  BTab,
  BTabs,
  VBToggle
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import DeleteConfirmation from '@/components/DeleteConfirmation'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import Loading from '@/components/Loading'
import { ColorPicker, Message, MessageBox } from 'element-ui'
import BasicBlankLine from '@/components/BasicBlankLine'
import BasicButton from '@/components/BasicButton'
import { confirm, copyCommand } from '@core/utils/common'

export default {
  components: {
    BasicButton,
    BasicBlankLine,
    Loading,
    AppTimelineItem,
    AppTimeline,
    BAvatar,
    BContainer,
    BTabs,
    BTab,
    BRow,
    BCol,
    BButton,
    BCardText,
    BInputGroupAppend,
    BInputGroup,
    BFormInput,
    FeatherIcon,
    DeleteConfirmation,
    ColorPicker
  },
  directives: {
    'b-toggle': VBToggle,
    Ripple
  },
  data: function() {
    return {
      entityType: 'organisation',
      editDisplay: false,
      editConfirm: true
    }
  },
  computed: {
    ...mapState('organisationDetailsModal', ['organisation', 'showDeleteModal', 'orgReport', 'organisationDetailLoading']),
    hasEditRights: function() {
      return true // All admins have edit rights
    },
    validName: function() {
      return this.validate()
    }
  },
  methods: {
    ...mapMutations('organisationDetailsModal', ['set_organisation_detail_loading']),
    secret(i) {
      return `secret ${i + 1}`
    },
    async onAddSecret() {
      try {
        const organisationId = this.organisation._id
        this.set_organisation_detail_loading(true)
        const secret = await this.$store.dispatch('organisation/getSecret', {
          organisationId
        })
        await MessageBox.alert(secret, 'secret', {
          confirmButtonText: 'copy'
        })
        copyCommand(secret)
        Message.success('copied')
        await this.$store.dispatch('organisationDetailsModal/get', { organisationId })
      } catch (e) {
        if (e) {
          console.error(e)
        }
      } finally {
        this.set_organisation_detail_loading(false)
      }
    },
    async onRemoveSecret(index) {
      try {
        const organisationId = this.organisation._id
        await confirm('confirm to delete?')
        await this.$store.dispatch('organisation/removeSecret', { organisationId, index })
        this.organisation.clientSecrets.splice(index, 1)
      } catch (e) {
        if (e) {
          console.error(e)
        }
      }
    },
    async onSaveBrand() {
      await this.$store.dispatch('organisationDetailsModal/updateOrganisation', {
        self: this,
        organisationId: this.organisation._id,
        name: this.organisation.name,
        brand: this.organisation.brand
      })
      this.closeSidebar()
    },
    validate() {
      if (this.organisation.name === ' ' || this.organisation.name === null || !this.organisation.name) {
        return false
      } else {
        return true
      }
    },
    closeSidebar() {
      this.$store.dispatch('organisationDetailsModal/onChangeModal', {
        self: this,
        visible: false
      })
    },
    async update() {
      await this.$store.dispatch('organisationDetailsModal/updateOrganisation', {
        self: this,
        organisationId: this.organisation._id,
        name: this.organisation.name
      })
      this.closeSidebar()
    },
    openDeleteConfirm(value) {
      this.$store.dispatch('organisationDetailsModal/setShowDeleteModal', {
        self: this,
        visible: value
      })
    },
    async deleteEntity(response) {
      if (response === true) {
        try {
          await this.$store.dispatch('organisationDetailsModal/delete', {
            self: this,
            organisationId: this.organisation._id
          })
        } catch (err) {
          console.log(err)
          this.$bvToast.toast('Check that you have permission to complete this action', {
            title: `Delete Failed`,
            toaster: 'b-toaster-top-center',
            solid: true
          })
        }
      }
    }
  }
}
</script>
<style lang='scss' scoped>
@import '@/assets/scss/variables/_variables';

.details .details-tab {
  .d-b-tabs {
    height: 100%;
    display: flex;
    flex-direction: column;

    ::v-deep .tab-content {
      flex: 1;
      overflow: hidden;
      margin-bottom: 0;

      & > div {
        height: 100%;
      }
    }
  }
}

.folder-detail-icon {
  height: 128px !important;
}

.b-sidebar-header {
  padding-left: 5%;
  padding-top: 2%;
  padding-bottom: 2%;
}

.container {
  text-align: center;
}

.modal-button {
  margin-right: 1.7%;
}

.switchDiv,
.infoDiv {
  display: flex;
  align-items: flex-start;
}

.switch {
  margin: auto -8px auto auto;
  font-weight: bold;
}

.primary-color {
  color: $purple;
}

.clear-margin {
  margin: 0;
}

// .details-tabs {
//   width: 20rem;
//   padding-bottom: 1rem;
// }

.row-section {
  padding-bottom: 1rem;
}

.link {
  cursor: pointer;
  text-decoration: underline;
  color: $purple;
  opacity: 0.7;
}

.custom-switch {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.b-avatar {
  margin: 0 auto;
  width: 50%;
  background-color: $purple;
}
</style>
